import { Component, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import { GiftService } from 'src/app/services/gift/gift.service';
import { GiftManageService } from 'src/app/services/gift/gift-manage.service';
import { CurrencyService } from 'src/app/services/general/currency.service';
import { PopupService } from 'src/app/services/general/popup.service';

import { Guest } from 'src/app/interfaces/guest';
import { Gift, GiftSummary } from 'src/app/interfaces/gift';
import { DeviceSafeAreaService } from 'src/app/services/device/device-safe-area.service';
import { TabService } from 'src/app/services/general/tab.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { FunctionService } from 'src/app/services/general/function.service';

/**
 * Gift new component
 */
@Component({
  selector: 'app-gift-new-component',
  templateUrl: './gift-new.component.html',
  styleUrls: ['./gift-new.component.scss'],
})
export class GiftNewComponent implements OnInit, OnDestroy {

  /**
   * Watch screen resize change
   */
  @HostListener('window:resize', ['$event']) onResize(event: any) {
    this.setupContentHeight();
  }
  /**
   * Watch screen orientation change
   */
  @HostListener('window:orientationchange', ['$event']) onOrientationChange(event: any) {
    this.setupContentHeight();
  }
  
  @ViewChild(CdkVirtualScrollViewport) cdkVirtualScrollViewport: CdkVirtualScrollViewport;
  /**
   * Header for getting header height
   */
  @ViewChild('header', { read: ElementRef }) header: ElementRef;
  /**
   * Footer for getting footer height
   */
  @ViewChild('footer', { read: ElementRef }) footer: ElementRef;

  /**
   * Page mode
   */
  @Input() pageMode: boolean;
  /**
   * Reset gift new component
   */
  @Input() set reset(reset: boolean) {
    if (reset) {
      this.initialize();
    }
  }

  /**
   * Gift list
   */
  giftList: Gift[];
  /**
   * Guest list
   */
  guestList: Guest[];
  /**
   * Gift summary of new gift
   */
  giftSummary: GiftSummary;
  /**
   * Total new gift
   */
  total: number;
  /**
   * Current gift index
   */
  index: number;
  /**
   * Preview flag for new gift before save.
   */
  preview: boolean;

  contentHeight: string;

  /**
   * Constructor
   * @param platform platform
   * @param modalController modal controller
   * @param translate translate service
   * @param giftService gift service
   * @param giftManageService gift manage service
   * @param currencyService currency service
   * @param popupService popup service
   */
  constructor(
    private ngZone: NgZone,
    private modalController: ModalController,
    private translate: TranslateService,
    private deviceSafeAreaService: DeviceSafeAreaService,
    private tabService: TabService,
    private giftService: GiftService,
    private giftManageService: GiftManageService,
    private currencyService: CurrencyService,
    private popupService: PopupService,
    private functionService: FunctionService,
  ) { }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
  }

  /**
   * Initialize
   */
  initialize() {
    this.index = 0;
    this.preview = false;
    this.total = 1;
    this.giftList = [];
    this.guestList = [];

    this.setupContentHeight();

    this.setupGiftList();
    this.getSummary();
  }

  /**
   * Before view enter
   */
  ionViewWillEnter() {
    this.initialize();
  }

  /**
   * Before view leave
   */
  ionViewWillLeave() {
  }

  /**
   * Go back
   */
  goBack() {
    if (this.preview) {
      this.preview = false;
      setTimeout(() => {
        this.setupContentHeight();
      }, 100);
    } else {
      this.dismissModal();
    }
  }

  async setupContentHeight() {
    if (this.pageMode) {
      await this.functionService.delay(300);
      if (this.header?.nativeElement?.offsetHeight) {
        this.contentHeight = 'calc(100vh - ' + (this.getHeaderHeight() + this.getFooterHeight()) + 'px)';
      } else {
        this.setupContentHeight();
      }
    } else {
      this.contentHeight = '100%';
    }
  }

  getScreenHeight() {
    return '100vh';
    // if (window?.innerHeight) {
    //   return window.innerHeight.toString() + 'px';
    // } else {
    //   return '100vh';
    // }
  }

  /**
   * Get header height
   */
  getHeaderHeight() {
    let header = 0;
    if (this.pageMode) {
      if (this.header?.nativeElement?.offsetHeight) {
        header = this.header.nativeElement.offsetHeight;
        // if (this.preview) {
        //   if (header < 172) {
        //     header = 172;
        //   }
        // } else {
        //   if (header > 134) {
        //     header = 134;
        //   }
        // }
        header += this.deviceSafeAreaService.getSafeArea();
      }
    }
    return header;
  }

  /**
   * Get footer height
   */
  getFooterHeight() {
    let footer = 0;
    if (this.pageMode) {
      if (this.preview) {
        if (this.footer?.nativeElement?.offsetHeight) {
          footer += this.footer.nativeElement.offsetHeight;
        }
        // if (!footer) {
        //   footer += 88;
        // }
      }
      
      if (this.tabService.showTab) {
        footer += 50;
      }
    }
    return footer;
  }

  /**
   * Get gift summary
   */
  getSummary() {
    this.giftSummary = {
      cash: 0,
      other: 0,
      amount: []
    };

    this.giftList?.forEach((gift: Gift) => {
      if (gift?.giftType === 'cash') {
        this.giftSummary.cash++;

        if (gift?.currency && gift?.amount) {
          const index = this.giftSummary?.amount?.findIndex(x => x?.currency === gift.currency);
          if (index === -1) {
            this.giftSummary.amount.push({
              currency: gift.currency,
              amount: gift.amount,
              count: 1
            });
          } else {
            this.giftSummary.amount[index].amount += gift.amount;
            this.giftSummary.amount[index].count++;
          }
        }
      } else if (gift?.giftType === 'other') {
        this.giftSummary.other++;
      }
    });
  }

  /**
   * Minus total
   */
  minusTotal() {
    if (!this.total) { this.total = 1; }
    if (this.total > 1) {
      this.total--;
      this.setupGiftList();
    } else {
      this.popupService.presentToast(this.translate.instant('VALIDATION.min_number', { number: 1 }), 'warning');
    }
  }

  /**
   * add total
   */
  addTotal() {
    if (!this.total) { this.total = 1; }
    this.total++;
    this.setupGiftList();
  }

  /**
   * Check total
   */
  async checkTotal() {
    this.total = this.total ? Number(this.total) : 1;
    if (!this.total) {
      this.total = 1;
      this.popupService.presentToast(this.translate.instant('VALIDATION.number_only'), 'warning');
    } else if (this.total < 1) {
      this.total = 1;
      this.popupService.presentToast(this.translate.instant('VALIDATION.min_number', { number: 1 }), 'warning');
    }
  }

  /**
   * Setup gift list
   */
  async setupGiftList() {
    await this.checkTotal();
    if (this.giftList?.length < this.total) {
      const count = this.total - this.giftList.length;
      const currency = this.currencyService.getAccountCurrency();
      for (let i = 0; i < count; i++) {
        this.giftList.push({
          giftId: this.giftService.getNewGiftId(),
          giftType: 'cash',
          name: '',
          qty: 1,
          currency: currency?.code ? currency.code : '',
          amount: 0,
          guestIdList: [],
          groupIdList: [],
          delete: false,
          createBy: null,
          updateBy: null
        });
      }
    } else if (this.giftList.length > this.total) {
      const count = this.giftList.length - this.total;
      for (let i = 0; i < count; i++) {
        this.giftList?.splice(this.giftList.length - 1, 1);
      }
    }
  }

  /**
   * Setup gift
   * @param data data
   * @param index index
   */
  setupGift(data: any, index: number) {
    if (data?.gift) {
      this.giftList[index] = data.gift;
    }
    if (data.next) {
      this.saveIndividually(data.gift);
      if (this.index + 1 >= this.total) {
        // this.save();
        this.setupPreview();
      } else {
        this.index++;
      }
    } else {
      if (this.index > 0) {
        this.index--;
      }
    }
  }

  /**
   * Setup preview
   */
  setupPreview() {
    this.getSummary();
    this.preview = true;
    this.setupViewport();
    setTimeout(() => {
      this.setupContentHeight();
    }, 100);
    
  }

  setupViewport() {
    if (this.cdkVirtualScrollViewport) {
      setTimeout(() => {
        this.cdkVirtualScrollViewport?.checkViewportSize();
      }, 200);
    } else {
      setTimeout(() => {
        this.setupViewport();
      }, 200);
    }
  }

  /**
   * Dismiss modal
   * @param gift Gift
   */
  async dismissModal(gift?: Gift) {
    if (this.modalController) {
      const modal = await this.modalController.getTop();
      if (modal) { this.modalController.dismiss({ gift }); }
    }
  }

  /**
   * Save
   */
  async save() {
    await this.popupService.presentLoading();
    if (this.giftList?.length) {
      const giftIdList = this.giftList.map((gift: Gift) => {
        return gift.giftId;
      });
      await this.giftManageService.saveGiftList(giftIdList, null, this.giftList, 'new');
      // this.initialize();
    }
    this.setupPreview();

    this.popupService.dismissLoading();
    this.popupService.saveSuccessToast();
  }

  async saveIndividually(gift: Gift) {
    await this.popupService.presentLoading();
    if (gift?.giftId) {
      const giftIdList = [ gift.giftId ];
      await this.giftManageService.saveGiftList(giftIdList, null, [ gift ], 'new');
      // this.initialize();
    }
    // this.setupPreview();

    this.popupService.dismissLoading();
    this.popupService.saveSuccessToast();
  }
  

  /**
   * Track item
   * @param index Index
   * @param item item
   */
  trackByFn(index: number, item: Gift) {
    if (item?.giftId) {
      return item?.giftId;
    }
    return index;
  }

}
